import { useRecoilState } from 'recoil';
import { authState, AuthState } from '@states/auth';
import { useCallback, useEffect } from 'react';
import type { AuthParams, UserCompany } from '@kiute-tech/network-kiute-pro';
import { auth as authClient } from '@utils/network/network';
import useSynchro from '@utils/hooks/agenda/useSynchro';
import { useIntercom } from 'react-use-intercom';
import { formatIntercomUser, setSentryUser } from '@utils/tracking';
import { navigateTo } from '@utils/navigation';
import { cleanSaasCookie } from '@utils/saasCookieManager';
import logs from '@utils/logs/firebase';
import { ROUTES } from '@utils/constants';

interface useAuthType {
    auth: AuthState;
    login: (payload: AuthParams) => Promise<{ error: string }>;
    logout: () => void;
    forgetpassword: (payload: AuthParams) => Promise<{ error: string }>;
}

const useAuth = (): useAuthType => {
    const [auth, setAuth] = useRecoilState(authState);
    const { hardReset } = useSynchro();
    const { boot, shutdown } = useIntercom();

    const getUserInfo = useCallback(async () => {
        setAuth((prevState) => ({ ...prevState, isLoadingUser: true }));
        const result = await authClient?.fetchUserCompany();
        const userInfo = (result?.data || {}) as UserCompany;
        if (userInfo?.id) {
            boot(formatIntercomUser(userInfo));
            setSentryUser({
                email: userInfo?.email || '',
                id: String(userInfo?.id),
            });
        } else {
            console.warn('fetchUserInfo error : ', result?.error);
        }

        setAuth((prevAuth) => ({
            ...prevAuth,
            userInfo,
            isLoadingUser: false,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(async (payload: AuthParams) => {
        hardReset();
        const result = await authClient.login(payload);

        if (result?.data) {
            const mkpToken = result.data?.marketplaceToken;
            setAuth((oldAuthState) => ({
                ...oldAuthState,
                loggedIn: true,
                marketplaceActivated: !!mkpToken,
            }));
            logs.login();
            void navigateTo(ROUTES.AGENDA);
        }

        const error: string = result?.error?.message || '';
        return { error };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const logout = useCallback(() => {
        void authClient?.logout();
        cleanSaasCookie();
        setAuth({
            loggedIn: false,
            userInfo: {} as UserCompany,
            isLoadingUser: false,
            marketplaceActivated: false,
        });
        logs.logout();
        hardReset();
        shutdown();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const forgetpassword = async (payload: AuthParams) => {
        const result = await authClient.forgotPassword(payload);
        if (result?.data) {
            void navigateTo(ROUTES.LOGIN);
        }

        const error: string = result?.error?.message || '';
        return { error };
    };

    useEffect(() => {
        if (auth.loggedIn && !auth.isLoadingUser && !auth?.userInfo?.id) {
            void getUserInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.loggedIn]);

    return {
        auth,
        login,
        logout,
        forgetpassword,
    };
};

export default useAuth;
